import { ROOT_API_URL } from "../config/apiConfig";

/**
 * Confirm and create a booking
 * @param {string} token - Auth token
 * @param {Object} payload - Booking payload
 * @param {string} idempotencyKey - Unique key for idempotency
 * @returns {Promise<Object>}
 */
export const confirmBooking = async (token, payload, idempotencyKey) => {
  try {
    const isB2C = token === localStorage.getItem("authToken") && !localStorage.getItem("companyAuthToken");
    const endpoint = isB2C ? `${ROOT_API_URL}/api/b2c/booking` : `${ROOT_API_URL}/api/bookings/confirm`;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-idempotency-key": idempotencyKey,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating booking:", error);
    throw error;
  }
};

export const downloadTravelDocument = async (token, bookingId, documentNo = null, type = "passenger") => {
  const response = await fetch(`${ROOT_API_URL}/api/bookings/${bookingId}/travel-document?type=${encodeURIComponent(type)}&_t=${Date.now()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });

  if (!response.ok) {
    let message = "Failed to download travel document.";
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // Keep default message for non-JSON errors.
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const safeDocumentNo = String(documentNo || bookingId || "travel-document").replace(/[^a-z0-9_-]/gi, "_");
  link.href = url;
  link.download = `Travel_Document_${safeDocumentNo}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Pre-validate booking limits against hierarchy
 * @param {string} token - Auth token
 * @param {Object} payload - Validation payload (totalFare, requiredTickets)
 * @returns {Promise<Object>}
 */
export const preValidateBookingLimits = async (token, payload) => {
  try {
    const isB2C = token === localStorage.getItem("authToken") && !localStorage.getItem("companyAuthToken");
    if (isB2C) {
      // B2C bookings do not have credit limit checks, return success immediately
      return { success: true, message: "B2C booking pre-validation skipped." };
    }

    const response = await fetch(`${ROOT_API_URL}/api/bookings/pre-validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, message: data.message || "Failed to validate booking limits." };
    }
    return data;
  } catch (error) {
    console.error("Pre-validate Booking Error:", error);
    return { success: false, message: "Network error during validation." };
  }
};
